/**
 * Distance is the only measurement this app shows, but it shows it in two unrelated
 * places - the result cards and the distance skip hints - so the conversion lives here
 * rather than in either view. Everything stored and sent stays in metres: units are a
 * display choice, and a radius that changed with the setting would change the search.
 */
export type Units = 'imperial' | 'metric';

export const DEFAULT_UNITS: Units = 'imperial';

const METRES_PER_MILE = 1609.344;
const METRES_PER_FOOT = 0.3048;

/** Under a tenth of a mile, miles round to "0.1 mi" and stop saying anything useful. */
const FEET_BELOW_M = METRES_PER_MILE / 10;

/** An exact-ish distance for a result card. */
export function formatDistance(metres: number, units: Units): string {
  if (units === 'metric') {
    if (metres < 1000) return `${Math.round(metres / 10) * 10} m`;
    return `${(metres / 1000).toFixed(metres < 10_000 ? 1 : 0)} km`;
  }

  if (metres < FEET_BELOW_M) {
    return `${Math.round(metres / METRES_PER_FOOT / 10) * 10} ft`;
  }
  const miles = metres / METRES_PER_MILE;
  return `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;
}

/**
 * A round number for "searches about X around you".
 *
 * Search radii are already approximations of a vague answer, so a decimal place here
 * would be false precision. Only used on radii of a kilometre or more, which is where
 * rounding to whole units still leaves something to say.
 */
export function formatRadius(metres: number, units: Units): string {
  if (units === 'metric') return `${Math.round(metres / 1000)} km`;

  const miles = Math.round(metres / METRES_PER_MILE);
  return `${miles} ${miles === 1 ? 'mile' : 'miles'}`;
}
